import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import type { FinancialCategoryItem } from '@/types/schemas';
import { v } from '@/theme/tokens';

interface CategorySelectorProps {
  categories: FinancialCategoryItem[];
  value: string | null;
  onSelect: (categoryId: string) => void;
  disabled?: boolean;
  labelledBy?: string;
}

/**
 * Searchable, hierarchical category picker.
 * Search matches name, code and the parent's name, case-insensitively —
 * Cyrillic included (toLocaleLowerCase('ru')).
 */
export function CategorySelector({
  categories,
  value,
  onSelect,
  disabled,
  labelledBy,
}: CategorySelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const byId = useMemo(
    () => new Map(categories.map((category) => [category.id, category])),
    [categories],
  );
  const selected = value ? byId.get(value) : undefined;

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ru');
    if (!needle) return categories;
    return categories.filter((category) => {
      const parent = category.parent_id ? byId.get(category.parent_id) : undefined;
      return (
        category.name_ru.toLocaleLowerCase('ru').includes(needle) ||
        category.code.toLocaleLowerCase('ru').includes(needle) ||
        (parent?.name_ru.toLocaleLowerCase('ru').includes(needle) ?? false)
      );
    });
  }, [categories, query, byId]);

  useEffect(() => {
    if (!open) return;
    const onOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      // Focus the search box as soon as the listbox opens.
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    // Keep the highlighted option visible (manual scroll — no scrollIntoView).
    const list = listRef.current;
    const item = list?.querySelector('[data-highlighted="true"]') as HTMLElement | null;
    if (!list || !item) return;
    const top = item.offsetTop - list.offsetTop;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (top + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = top + item.offsetHeight - list.clientHeight;
    }
  }, [highlight]);

  const choose = (category: FinancialCategoryItem) => {
    onSelect(category.id);
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((current) => Math.min(current + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = filtered[highlight];
      if (item) choose(item);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', minWidth: 0 }}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelledBy}
        onClick={() => setOpen((current) => !current)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
          maxWidth: 280,
          padding: '8px 11px',
          border: `1px solid ${v('line')}`,
          borderRadius: 9,
          background: v('surface'),
          color: selected ? v('ink') : v('ink-muted'),
          fontSize: 13,
          fontFamily: 'inherit',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.name_ru : 'Выбрать категорию…'}
        </span>
        <Icon name="chevron" size={14} color={v('ink-muted')} />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 50,
            top: 'calc(100% + 4px)',
            left: 0,
            width: 'min(320px, 88vw)',
            background: v('surface'),
            border: `1px solid ${v('line')}`,
            borderRadius: 12,
            boxShadow: v('shadow-strong'),
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: 8, borderBottom: `1px solid ${v('line-soft')}` }}>
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setHighlight(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Поиск: название, код, группа…"
              aria-label="Поиск категории"
              style={{
                width: '100%',
                padding: '8px 10px',
                border: `1px solid ${v('line')}`,
                borderRadius: 8,
                background: v('panel'),
                color: v('ink'),
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div ref={listRef} role="listbox" className="scroll-area" style={{ maxHeight: 260, overflowY: 'auto', padding: 4 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '12px 10px', fontSize: 13, color: v('ink-muted') }}>
                Ничего не найдено
              </div>
            )}
            {filtered.map((category, index) => {
              const active = category.id === value;
              const highlighted = index === highlight;
              return (
                <div
                  key={category.id}
                  role="option"
                  aria-selected={active}
                  data-highlighted={highlighted ? 'true' : undefined}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => choose(category)}
                  style={{
                    padding: '7px 10px',
                    paddingLeft: 10 + category.level * 16,
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: category.level === 0 ? 600 : 500,
                    color: active ? v('accent-ink') : v('ink-sub'),
                    background: highlighted ? v('panel') : active ? v('accent-soft') : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {category.name_ru}
                  </span>
                  {active && <Icon name="check" size={14} color={v('accent')} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
